import React from 'react'

/**
 * FOLDER BROWSER LOADING SKELETON
 *
 * Shimmer-animated placeholder matching the FolderBrowser two-panel layout.
 * Used while release data and folder tree are loading on the scenario list page.
 *
 * Structure mirrors the real page:
 * - Page header (breadcrumb bar + "New Scenario" button)
 * - folder-browser-wrapper
 *   - folder-browser-content (white rounded card)
 *     - Left panel: folder sidebar skeleton (280px)
 *     - Right panel: scenario panel skeleton (flex: 1)
 *
 * Reuses existing .skeleton-bone class and skeletonShimmer animation from index.css.
 *
 * Exports:
 * - FolderBrowserSkeleton: Full-page skeleton for initial load
 * - ScenarioPanelSkeleton: Right-panel-only skeleton for folder switch loading
 */

/**
 * FOLDER SIDEBAR SKELETON (internal)
 *
 * Mimics the FolderSidebar component:
 * - Header: "FOLDERS" label + "Add folder" button
 * - "All Scenarios" row
 * - 6 folder rows at varying nesting levels with chevron + folder icon + name
 */
function FolderSidebarSkeleton() {
  return (
    <div
      className="folder-sidebar"
      style={{
        width: '280px',
        minWidth: '280px',
        borderRight: '1px solid var(--ds-border, #DFE1E6)',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px'
      }}
    >
      {/* Sidebar Header - "FOLDERS" label + "Add folder" button */}
      <div
        className="folder-sidebar-header"
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* "FOLDERS" label placeholder */}
        <div className="skeleton-bone" style={{ width: '52px', height: '11px' }} />
        {/* "Add folder" button placeholder */}
        <div className="skeleton-bone" style={{ width: '80px', height: '28px', borderRadius: '3px' }} />
      </div>

      {/* Folder List */}
      <div
        className="folder-list"
        style={{
          flex: 1,
          overflow: 'hidden',
          padding: '4px 0'
        }}
      >
        {/* "All Scenarios" row placeholder */}
        <div style={{ padding: '6px 12px' }}>
          <div className="skeleton-bone" style={{ width: '72px', height: '11px' }} />
        </div>

        {/* Folder rows - varying widths and nesting levels */}
        {/* Level 0 folder */}
        <div className="fb-skeleton-folder-row" style={{ padding: '4px 8px 4px 4px' }}>
          <div className="skeleton-bone" style={{ width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0 }} />
          <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <div className="skeleton-bone" style={{ width: '65%', height: '13px' }} />
        </div>

        {/* Level 1 (nested) folder */}
        <div className="fb-skeleton-folder-row" style={{ padding: '4px 8px 4px 16px' }}>
          <div className="skeleton-bone" style={{ width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0 }} />
          <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <div className="skeleton-bone" style={{ width: '55%', height: '13px' }} />
        </div>

        {/* Level 1 (nested) folder */}
        <div className="fb-skeleton-folder-row" style={{ padding: '4px 8px 4px 16px' }}>
          <div className="skeleton-bone" style={{ width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0 }} />
          <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <div className="skeleton-bone" style={{ width: '40%', height: '13px' }} />
        </div>

        {/* Level 0 folder */}
        <div className="fb-skeleton-folder-row" style={{ padding: '4px 8px 4px 4px' }}>
          <div className="skeleton-bone" style={{ width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0 }} />
          <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <div className="skeleton-bone" style={{ width: '50%', height: '13px' }} />
        </div>

        {/* Level 0 folder */}
        <div className="fb-skeleton-folder-row" style={{ padding: '4px 8px 4px 4px' }}>
          <div className="skeleton-bone" style={{ width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0 }} />
          <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <div className="skeleton-bone" style={{ width: '70%', height: '13px' }} />
        </div>

        {/* Level 1 (nested) folder */}
        <div className="fb-skeleton-folder-row" style={{ padding: '4px 8px 4px 16px' }}>
          <div className="skeleton-bone" style={{ width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0 }} />
          <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <div className="skeleton-bone" style={{ width: '45%', height: '13px' }} />
        </div>
      </div>
    </div>
  )
}

/**
 * SCENARIO PANEL SKELETON
 *
 * Shimmer-animated placeholder for the right panel (scenario list).
 * Used both as part of the full-page skeleton and standalone
 * when switching folders (sidebar already loaded, only scenarios refetching).
 *
 * Mimics ScenarioPanel layout:
 * - Header: title + count + Import/Search buttons
 * - 7 scenario card rows with icon, key, name, progress bar, priority, avatar, counts
 */
export function ScenarioPanelSkeleton() {
  // Varying name widths for visual naturalness
  const scenarioNameWidths = [70, 55, 45, 60, 50, 40, 65]

  return (
    <div
      className="scenario-panel"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        overflow: 'hidden',
        borderTopRightRadius: '8px',
        borderBottomRightRadius: '8px'
      }}
    >
      {/* Panel Header - title + count + buttons */}
      <div
        className="scenario-panel-header"
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* Title + count placeholder */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <div className="skeleton-bone" style={{ width: '100px', height: '14px' }} />
          <div className="skeleton-bone" style={{ width: '30px', height: '12px' }} />
        </div>
        {/* Import + Search button placeholders */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <div className="skeleton-bone" style={{ width: '80px', height: '32px', borderRadius: '3px' }} />
          <div className="skeleton-bone" style={{ width: '80px', height: '32px', borderRadius: '3px' }} />
        </div>
      </div>

      {/* Scenario Card Rows */}
      <div
        className="scenario-list"
        style={{
          flex: 1,
          overflow: 'hidden',
          padding: '0'
        }}
      >
        {scenarioNameWidths.map((nameWidth, index) => (
          <div key={index} className="fb-skeleton-scenario-row">
            {/* Left side: document icon + scenario key + name */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Document icon placeholder */}
              <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px' }} />
              {/* Scenario key placeholder (e.g., "POJ-1") */}
              <div className="skeleton-bone" style={{ width: '50px', height: '12px' }} />
              {/* Scenario name placeholder - varying widths */}
              <div className="skeleton-bone" style={{ width: `${nameWidth}%`, height: '14px' }} />
            </div>
            {/* Right side: progress bar + priority + avatar + counts */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, marginLeft: 'auto' }}>
              {/* Progress bar placeholder (120px wide, 4px tall) */}
              <div className="skeleton-bone" style={{ width: '120px', height: '4px', borderRadius: '2px' }} />
              {/* Priority lozenge placeholder */}
              <div className="skeleton-bone" style={{ width: '55px', height: '18px', borderRadius: '3px' }} />
              {/* Assignee avatar placeholder */}
              <div className="skeleton-bone skeleton-circle" style={{ width: '24px', height: '24px' }} />
              {/* Test case count placeholder */}
              <div className="skeleton-bone" style={{ width: '42px', height: '11px' }} />
              {/* Run count placeholder */}
              <div className="skeleton-bone" style={{ width: '32px', height: '11px' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * FOLDER BROWSER SKELETON (full page)
 *
 * Complete page skeleton shown during initial load when
 * releaseLoading or foldersLoading is true.
 * Mirrors the entire FolderBrowser layout:
 * page header + two-panel content (sidebar + scenario panel).
 */
export function FolderBrowserSkeleton() {
  return (
    <div
      className="folder-browser-page"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        overflow: 'hidden'
      }}
    >
      {/* Page Header Skeleton - breadcrumbs + "New Scenario" button */}
      <div
        className="page-header"
        style={{
          padding: '8px 20px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* Breadcrumb placeholder: Project / Releases / Release */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div className="skeleton-bone" style={{ width: '60px', height: '14px' }} />
          <span style={{ color: 'var(--ds-text-subtlest, #A5ADBA)', fontSize: '14px' }}>/</span>
          <div className="skeleton-bone" style={{ width: '60px', height: '14px' }} />
          <span style={{ color: 'var(--ds-text-subtlest, #A5ADBA)', fontSize: '14px' }}>/</span>
          <div className="skeleton-bone" style={{ width: '90px', height: '14px' }} />
        </div>
        {/* "New Scenario" button placeholder */}
        <div className="skeleton-bone" style={{ width: '120px', height: '32px', borderRadius: '3px' }} />
      </div>

      {/* Two-Panel Content - uses real layout classes for identical dimensions */}
      <div className="folder-browser-wrapper">
        <div className="folder-browser-content">
          {/* Left Panel - Folder Sidebar Skeleton */}
          <FolderSidebarSkeleton />
          {/* Right Panel - Scenario Panel Skeleton */}
          <ScenarioPanelSkeleton />
        </div>
      </div>
    </div>
  )
}
